import React, { useEffect, useRef, useState } from 'react'
import { GearIcon } from '@/atoms/Icons/GearIcon'
import { AUTO_QUALITY_LABEL, DEFAULT_PLAYBACK_SPEED, PLAYBACK_SPEEDS } from '@/constants/video'
import { useTranslate } from '@/utils/translate/translate-client'
import { useHlsPlayerUI } from './HlsPlayerUIContext'
import { HlsSettingsPanel } from './HlsSettingsPanel'

/**
 * Settings button + panel for the HLS.js player control bar.
 * Opens a settings panel with sub-pages for quality, audio, subtitles, and playback speed.
 */
export const HlsSettingsButton: React.FC = () => {
  const { t } = useTranslate('common')
  const { controller, playbackSpeed } = useHlsPlayerUI()
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const togglePanel = () => {
    setOpen((prev) => !prev)
  }

  const closePanel = () => {
    setOpen(false)
  }

  // Close the panel when clicking outside of both the button and the portaled panel
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      // Check if click is inside the button wrapper
      if (panelRef.current?.contains(target)) return
      // Check if click is inside the portaled settings panel
      const portalPanel = document.getElementById('hlsjs-player')?.querySelector('[data-hls-settings-panel]')
      if (portalPanel?.contains(target)) return
      setOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // Read track lists from the controller each time the panel opens
  const audioTracks = open ? controller?.getAvailableAudio() ?? [] : []
  const subtitleTracks = open ? controller?.getSubtitleList() ?? [] : []
  const qualities = open ? controller?.getAvailableQualities() ?? [] : []
  const currentQualityIndex = open ? controller?.getCurrentQualityIndex() ?? -1 : -1
  const activeAudioId = open ? controller?.getCurrentAudioId() ?? '' : ''

  // Current active subtitle track
  const activeSubtitleId = subtitleTracks.find((track) => track.enabled)?.id ?? 'off'

  // Current quality label
  const currentQualityLabel = (() => {
    if (currentQualityIndex < 0) return AUTO_QUALITY_LABEL
    const quality = qualities.find((q) => q.index === currentQualityIndex)
    return quality?.label ?? AUTO_QUALITY_LABEL
  })()

  // Current speed label
  const currentSpeedLabel = playbackSpeed === DEFAULT_PLAYBACK_SPEED ? t('normal', 'Normal') : `${playbackSpeed}x`

  // Current audio label
  const currentAudioLabel = (() => {
    if (audioTracks.length === 0) return t('default', 'Default')
    const active = audioTracks.find((track) => track.id === activeAudioId)
    return active?.label ?? t('default', 'Default')
  })()

  // Current subtitle label
  const currentSubtitleLabel = (() => {
    if (activeSubtitleId === 'off') return t('off', 'Off')
    const active = subtitleTracks.find((track) => track.id === activeSubtitleId)
    return active?.label ?? t('off', 'Off')
  })()

  // Build options for the main menu
  const options = (() => {
    const result = []

    if (qualities.length > 1) {
      result.push({
        id: 'quality',
        label: t('quality', 'Quality'),
        currentValue: currentQualityLabel,
        page: 'quality' as const,
      })
    }

    if (audioTracks.length > 1) {
      result.push({
        id: 'audio',
        label: t('audio', 'Audio'),
        currentValue: currentAudioLabel,
        page: 'audio' as const,
      })
    }

    if (subtitleTracks.length > 0) {
      result.push({
        id: 'subtitles',
        label: t('subtitles', 'Subtitles'),
        currentValue: currentSubtitleLabel,
        page: 'subtitles' as const,
      })
    }

    result.push({
      id: 'speed',
      label: t('speed', 'Speed'),
      currentValue: currentSpeedLabel,
      page: 'speed' as const,
    })

    return result
  })()

  // Build sub-page data
  const pages = {
    quality: {
      title: t('quality', 'Quality'),
      items: [
        { id: 'auto', label: AUTO_QUALITY_LABEL, active: currentQualityIndex < 0 },
        ...qualities
          .sort((a, b) => b.height - a.height)
          .map((q) => ({
            id: String(q.index),
            label: q.label,
            active: q.index === currentQualityIndex,
          })),
      ],
      onSelect: (id: string) => {
        if (id === 'auto') {
          controller?.setQuality(-1)
        } else {
          controller?.setQuality(Number(id))
        }
      },
    },
    audio: {
      title: t('audio', 'Audio'),
      items: audioTracks.map((track) => ({
        id: track.id,
        label: track.label,
        active: track.id === activeAudioId,
      })),
      onSelect: (id: string) => {
        controller?.setAudio(id)
      },
    },
    subtitles: {
      title: t('subtitles', 'Subtitles'),
      items: [
        { id: 'off', label: t('off', 'Off'), active: activeSubtitleId === 'off' },
        ...subtitleTracks.map((track) => ({
          id: track.id,
          label: track.label,
          active: track.id === activeSubtitleId,
        })),
      ],
      onSelect: (id: string) => {
        if (id === 'off') {
          controller?.disableSubTitle()
        } else {
          controller?.enableSubTitle(id)
        }
      },
    },
    speed: {
      title: t('speed', 'Speed'),
      items: PLAYBACK_SPEEDS.map((speed) => ({
        id: String(speed),
        label: speed === DEFAULT_PLAYBACK_SPEED ? t('normal', 'Normal') : `${speed}x`,
        active: playbackSpeed === speed,
      })),
      onSelect: (id: string) => {
        controller?.setPlaybackSpeed(Number(id))
      },
    },
  }

  return (
    <div ref={panelRef} className="relative">
      <button
        type="button"
        className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/20"
        onClick={togglePanel}
        aria-label={t('settings', 'Settings')}
      >
        <GearIcon size={22} color="white" />
      </button>
      <HlsSettingsPanel open={open} onClose={closePanel} options={options} pages={pages} />
    </div>
  )
}
